'use client';
import { useState } from 'react';
import Image from 'next/image';
import type { Squad } from '@/lib/tourData';

interface SquadsTabProps {
  squads: Squad[];
}

const roleColors: Record<string, string> = {
  'Batsman': 'text-rc-blue bg-rc-blue/10',
  'Bowler': 'text-rc-orange bg-rc-orange/10',
  'All-Rounder': 'text-rc-green bg-rc-green/10',
  'Wicket-Keeper': 'text-yellow-400 bg-yellow-400/10',
};

export default function SquadsTab({ squads }: SquadsTabProps) {
  const [selectedTeamId, setSelectedTeamId] = useState(squads?.[0]?.team?.id || '');

  const selectedSquad = squads?.find(s => s.team.id === selectedTeamId);

  return (
    <div>
      {/* Team selector */}
      <div className="flex gap-2 flex-wrap mb-6">
        {squads?.map(squad => (
          <button
            key={squad.team.id}
            onClick={() => setSelectedTeamId(squad.team.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              selectedTeamId === squad.team.id
                ? 'bg-rc-orange text-white' :'bg-rc-card border border-rc-border text-white/60 hover:text-white hover:border-white/30'
            }`}
          >
            {squad.team.logo ? (
              <div className="w-5 h-5 rounded-full overflow-hidden">
                <Image src={squad.team.logo} alt={`${squad.team.name} logo`} width={20} height={20} className="w-full h-full object-cover" unoptimized />
              </div>
            ) : (
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold"
                style={{ backgroundColor: squad.team.color + '44' }}
              >
                {squad.team.shortName.slice(0, 1)}
              </div>
            )}
            <span className="hidden sm:inline">{squad.team.shortName}</span>
          </button>
        ))}
      </div>

      {/* Selected team info */}
      {selectedSquad && (
        <div>
          <div className="flex items-center gap-3 mb-5">
            {selectedSquad.team.logo ? (
              <div className="w-10 h-10 rounded-full overflow-hidden bg-rc-border">
                <Image src={selectedSquad.team.logo} alt={`${selectedSquad.team.name} logo`} width={40} height={40} className="w-full h-full object-cover" unoptimized />
              </div>
            ) : (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ backgroundColor: selectedSquad.team.color + '33', border: `1px solid ${selectedSquad.team.color}66` }}
              >
                {selectedSquad.team.shortName}
              </div>
            )}
            <div>
              <h3 className="text-white font-semibold text-base">{selectedSquad.team.name}</h3>
              <p className="text-white/40 text-xs">{selectedSquad.players?.length} Players</p>
            </div>
          </div>

          {/* Players grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {selectedSquad.players?.map(player => (
              <div
                key={player.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-rc-border hover:border-white/20 transition-colors"
                style={{ backgroundColor: 'rgb(18, 23, 33)' }}
              >
                <div className="relative flex-shrink-0">
                  <div className="w-11 h-11 rounded-full overflow-hidden bg-rc-border">
                    <Image
                      src={player.image}
                      alt={`${player.name} cricket player photo`}
                      width={44}
                      height={44}
                      className="w-full h-full object-cover"
                      unoptimized
                    />
                  </div>
                  {player.isCaptain && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rc-orange flex items-center justify-center text-[9px] font-black text-white">C</span>
                  )}
                  {player.isViceCaptain && !player.isCaptain && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rc-blue flex items-center justify-center text-[9px] font-black text-white">VC</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-white/85 text-sm font-medium truncate">{player.name}</p>
                    {player.isOverseas && (
                      <span className="flex-shrink-0 text-[9px] px-1 py-0.5 rounded bg-white/10 text-white/50 font-medium">OS</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${roleColors[player.role] || 'text-white/50 bg-white/10'}`}>
                      {player.role}
                    </span>
                  </div>
                  <p className="text-white/30 text-[10px] mt-0.5">{player.country}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
